import { memo, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  CartContainer,
  IconHighlight,
  Input,
  ModalLayout,
  Skeleton,
  StatusMessage,
  useToast,
} from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useDisconnectImpact } from '../../hooks/provider';
import { IMPACT_GROUPS, disconnectProvider } from '../../../../services';
import { AlertTriangle, CheckCircle } from 'lucide-react';

const MAX_VISIBLE = 5;

const stopPropagation = (e) => e.stopPropagation();

const confirmLabel = (t, total) => {
  if (total === 0) return t('integrations.disconnect.confirm');
  if (total === 1) return t('integrations.disconnect.confirmBlockedOne');
  return t('integrations.disconnect.confirmBlocked', { count: total });
};

const ImpactGroupCard = memo(function ImpactGroupCard({ group, items, t }) {
  const shown = items.slice(0, MAX_VISIBLE);
  const more = items.length - shown.length;

  return (
    <CartContainer size="S" variant="red" className="gap-0 !p-0">
      <div className="flex items-center gap-2 border-b border-red-500/20 px-3 py-2">
        <span className="flex-1 text-[10.5px] font-extrabold uppercase tracking-[0.06em] text-red-400">
          {t(`integrations.disconnect.groups.${group}`)}
        </span>
        <Badge variant="danger" size="small" radius="full">
          {items.length}
        </Badge>
      </div>
      <ul className="flex flex-col gap-1.5 px-3 py-2.5">
        {shown.map((name) => (
          <li key={name} className="flex items-center gap-2 text-[13px] leading-snug text-content">
            <span className="size-1.5 shrink-0 rounded-full bg-red-500 opacity-75" aria-hidden />
            {name}
          </li>
        ))}
        {more > 0 ? (
          <li className="pl-3.5 text-xs italic text-content-muted">
            {t('integrations.disconnect.andMore', { count: more })}
          </li>
        ) : null}
      </ul>
    </CartContainer>
  );
});

/**
 * Disconnect impact alert (CT-4187). Prototype `openImpactModal`: critical
 * confirmation with grouped consumers (workflows / agents / networks / widgets),
 * type-to-confirm, then `disconnectProvider`.
 */
function DisconnectImpactAlert({ isOpen, slug, providerName, onClose, onDisconnected }) {
  const { t } = useTranslation('admin-studio');
  const toast = useToast();
  const { impact, isLoading, error } = useDisconnectImpact(slug, isOpen);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const submittingRef = useRef(false);

  useEffect(() => {
    if (isOpen) setConfirmName('');
  }, [isOpen]);

  if (!isOpen) return null;

  const total = impact == null ? 0 : (impact.total ?? IMPACT_GROUPS.reduce((sum, key) => sum + (impact[key]?.length ?? 0), 0));
  const isEmptyImpact = Boolean(impact) && !isLoading && !error && total === 0;
  const nameMatches = confirmName.trim().toLowerCase() === String(providerName).trim().toLowerCase();
  const canConfirm = Boolean(impact) && !error && !isLoading && !isSubmitting && nameMatches;

  const confirm = async () => {
    if (!canConfirm || submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      await disconnectProvider(slug);
      toast.success({
        message:
          total > 0
            ? t('integrations.disconnect.toastBlocked', { name: providerName, count: total })
            : t('integrations.disconnect.toast', { name: providerName }),
        canClose: true,
      });
      onDisconnected?.();
      onClose?.();
    } catch (err) {
      toast.error({ message: err?.message ?? t('common.requestFailed'), canClose: true });
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  let body;
  if (isLoading) {
    body = <Skeleton className="h-32 w-full" />;
  } else if (error || !impact) {
    body = <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />;
  } else if (total === 0) {
    body = (
      <div className="flex items-start gap-3 rounded-[10px] border border-edge bg-surface-muted/30 p-4 text-[13px] leading-relaxed text-content-muted">
        <IconHighlight icon={CheckCircle} size="small" bgColor="Success" iconColor="Green" />
        <p>{t('integrations.disconnect.empty', { name: providerName })}</p>
      </div>
    );
  } else {
    body = (
      <>
        <p className="text-[15px] font-extrabold tracking-tight text-red-400">
          {total === 1
            ? t('integrations.disconnect.headlineOne')
            : t('integrations.disconnect.headline', { count: total })}
        </p>
        <p className="mb-4 mt-1 text-xs leading-relaxed text-content-muted">
          {t('integrations.disconnect.body')}
        </p>
        <div className="flex flex-col gap-2.5">
          {IMPACT_GROUPS.map((group) => {
            const items = impact[group] ?? [];
            if (items.length === 0) return null;
            return <ImpactGroupCard key={group} group={group} items={items} t={t} />;
          })}
        </div>
      </>
    );
  }

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      closeDisabled={isSubmitting}
      useBodyPortal
      maxWidthClassName="max-w-[560px]"
      title={t('integrations.disconnect.title', { name: providerName })}
      subtitle={
        isEmptyImpact
          ? t('integrations.disconnect.subtitleEmpty')
          : t('integrations.disconnect.subtitle')
      }
      titlePrefix={
        <IconHighlight
          icon={AlertTriangle}
          size="small"
          bgColor="Error"
          iconColor="Error"
          className="border border-red-500/40"
        />
      }
      panelClassName="border-red-500/35 border-t-[3px] border-t-red-500"
    >
      <div className="max-h-[60vh] overflow-y-auto px-5 py-5">{body}</div>

      <div className="flex flex-col gap-3 border-t border-edge px-5 py-3.5">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="disconnect-confirm-input" className="text-xs leading-relaxed text-content-muted">
            {t('integrations.disconnect.typeToConfirm')}{' '}
            <b className="font-mono font-semibold text-red-400">{providerName}</b>
          </label>
          <Input
            id="disconnect-confirm-input"
            placeholder={providerName}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            value={confirmName}
            disabled={isSubmitting || isLoading || Boolean(error)}
            onChange={(e) => setConfirmName(e.target.value)}
            onKeyDown={(e) => {
              stopPropagation(e);
              if (e.key === 'Enter') {
                e.preventDefault();
                confirm();
              }
            }}
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting}>
            {t('integrations.disconnect.keepConnected')}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={confirm}
            loading={isSubmitting}
            disabled={!canConfirm}
          >
            {confirmLabel(t, total)}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}

export default DisconnectImpactAlert;
